<template>
    <div data-testid="bookclosure-project-list" class="page-table-layout">
        <v-card data-testid="bookclosure-project-list__chart-of-accounts-prompt" v-if="isFirst" width="70%" class="ma-auto mt-10" :style="{ backgroundColor: '#ffffff' }">
            <v-card-text class="">
                <div data-testid="bookclosure-project-list__prompt-balloon" class="" :style="promptStyles">
                    <h4 data-testid="bookclosure-project-list__prompt-heading" class="font-weight-regular noTransform d-flex align-center ">
                      <v-icon data-testid="bookclosure-project-list__prompt-icon"> mdi-arrow-right-bold</v-icon>
                      Set Up Chart of Accounts: {{ promptMessage }}
                      <v-icon data-testid="bookclosure-project-list__prompt-close-btn" @click="hidePrompt">mdi mdi-close-circle</v-icon>
                    </h4>
                </div>
            </v-card-text>
        </v-card>
        <br v-if="isFirst">
        <v-card data-testid="bookclosure-project-list__main-card" :loading="loading" class="all-width flex-card shadow-off" :disabled="loading" loader-height="2">
            <v-card-text class="page-table-layout__body">
                <!-- Skeleton Loader for Initial Loading -->
                <div data-testid="bookclosure-project-list__skeleton-loader" v-if="initialLoading">
                    <v-row class="mb-4">
                        <v-col cols="4">
                            <v-skeleton-loader type="text" class="mb-2"></v-skeleton-loader>
                        </v-col>
                        <v-col cols="4">
                            <v-skeleton-loader type="text" class="mb-2"></v-skeleton-loader>
                        </v-col>
                        <v-col cols="4" class="d-flex justify-end">
                            <v-skeleton-loader type="button" width="100"></v-skeleton-loader>
                            <v-skeleton-loader type="button" width="120" class="ml-2"></v-skeleton-loader>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-skeleton-loader type="table-heading, table-row-divider@10"></v-skeleton-loader>
                        </v-col>
                    </v-row>
                </div>

                <!-- Main Content (visible when not initial loading) -->
                <div data-testid="bookclosure-project-list__header" class="d-flex align-center  justify-space-between mb-4" v-if="!initialLoading">

                    <div class=""></div>

                    <div data-testid="bookclosure-project-list__filter-section" class="d-flex align-center justify-center">
                        <div data-testid="bookclosure-project-list__filter-container" class="border-on d-flex shadow-on radius-small">
                            <div data-testid="bookclosure-project-list__year-filter" class="text-center px-4 py-2 hoverable">
                                <div class="grey--text text--darken-1 line-1">Year</div>
                                <div class="font-weight-bold">
                                    <v-menu data-testid="bookclosure-project-list__year-menu" offset-y v-if="(filterData.created_year).length > 1" open-on-hover>
                                        <template v-slot:activator="{ on, attrs }">
                                            <div data-testid="bookclosure-project-list__year-activator" class="filter-hide cursor-pointer" v-bind="attrs" v-on="on"><span data-testid="bookclosure-project-list__year-value">{{ (filterSelectedData || {}).selectedyear }}</span>
                                                <v-icon data-testid="bookclosure-project-list__year-icon" class="">mdi-chevron-down</v-icon>
                                            </div>
                                        </template>
                                        <perfect-scrollbar data-testid="bookclosure-project-list__year-scrollbar" style="max-height: 300px">
                                            <v-list data-testid="bookclosure-project-list__year-list" dense class="pa-0 border-left-default">
                                                <v-list-item 
                                                    data-testid="bookclosure-project-list__year-item"
                                                    v-for="(v, k) in filterData.created_year" 
                                                    :key="k"
                                                    @click="filterWithYear(v)">
                                                    <v-list-item-title data-testid="bookclosure-project-list__year-item-title">{{ v || "NA" }}</v-list-item-title>
                                                </v-list-item>
                                            </v-list>
                                        </perfect-scrollbar>
                                    </v-menu>
                                    <span data-testid="bookclosure-project-list__year-static" v-else>{{ (filterData || {}).created_at || "NA" }}</span>
                                </div>
                            </div>
                            <div data-testid="bookclosure-project-list__month-filter" class="text-center px-4 py-2 hoverable">
                                <div class="grey--text text--darken-1 line-1">Month</div>
                                <div class="font-weight-bold">
                                    <v-menu data-testid="bookclosure-project-list__month-menu" offset-y v-if="filterData.created_month.length > 1" open-on-hover>
                                        <template v-slot:activator="{ on, attrs }">
                                            <div data-testid="bookclosure-project-list__month-activator" class="filter-hide cursor-pointer" v-bind="attrs" v-on="on"><span data-testid="bookclosure-project-list__month-value">{{ (filterSelectedData || {}).selectedmonth }}</span>
                                                <v-icon data-testid="bookclosure-project-list__month-icon" class="">mdi-chevron-down</v-icon>
                                            </div>
                                        </template>
                                        <perfect-scrollbar data-testid="bookclosure-project-list__month-scrollbar" style="max-height: 300px">
                                            <v-list data-testid="bookclosure-project-list__month-list" dense class="pa-0 border-left-default">
                                                <v-list-item 
                                                    data-testid="bookclosure-project-list__month-item"
                                                    v-for="(v, k) in filterData.created_month" 
                                                    :key="k"
                                                    @click="filterByMonth(v)">
                                                    <v-list-item-title data-testid="bookclosure-project-list__month-item-title">{{ v || "NA" }}</v-list-item-title>
                                                </v-list-item>
                                            </v-list>
                                        </perfect-scrollbar>
                                    </v-menu>
                                    <span data-testid="bookclosure-project-list__month-static" v-else>{{ monthNames[(filterData || {}).created_month || "NA"] }}</span>
                                </div>
                            </div>

                            <div class="text-center px-4 py-2 hoverable">
                                <div class="grey--text text--darken-1 line-1">Locations</div>
                                <div class="font-weight-bold">
                                    <v-menu offset-y v-if="filterData.location.length > 1" open-on-hover>
                                        <template v-slot:activator="{ on, attrs }">
                                            <div class="filter-hide cursor-pointer" v-bind="attrs" v-on="on">
                                                <span v-if="(filterSelectedData || {}).selectedlocation.length === 0 || ((filterSelectedData || {}).selectedlocation.length === 1 && (filterSelectedData || {}).selectedlocation[0] === 'All')">All</span>
                                                <span v-else-if="(filterSelectedData || {}).selectedlocation.length === 1">{{ (filterSelectedData || {}).selectedlocation[0].name }}</span>
                                                <span v-else>{{ (filterSelectedData || {}).selectedlocation.length }} Locations</span>
                                                <v-icon class="">mdi-chevron-down</v-icon>
                                            </div>
                                        </template>
                                        <perfect-scrollbar style="max-height: 300px">
                                            <v-list dense class="pa-0 border-left-default">
                                                <v-list-item @click="toggleAllLocations()" :key="'all'">
                                                    <v-list-item-action class="mr-2">
                                                        <v-checkbox 
                                                            :input-value="isAllLocationsSelected" 
                                                            @click.stop="toggleAllLocations()"
                                                            hide-details
                                                            dense
                                                        ></v-checkbox>
                                                    </v-list-item-action>
                                                    <v-list-item-title>All</v-list-item-title>
                                                </v-list-item>
                                                <v-divider></v-divider>
                                                <template v-for="(v, k) in filterData.location">
                                                    <v-list-item v-if="v !== 'All'" @click="toggleLocation(v)" :key="k">
                                                        <v-list-item-action class="mr-2">
                                                            <v-checkbox 
                                                                :input-value="isLocationSelected(v)" 
                                                                @click.stop="toggleLocation(v)"
                                                                hide-details
                                                                dense
                                                            ></v-checkbox>
                                                        </v-list-item-action>
                                                        <v-list-item-title>{{ (v || "NA").name || "NA" }}</v-list-item-title>
                                                    </v-list-item>
                                                </template>
                                            </v-list>
                                        </perfect-scrollbar>
                                    </v-menu>
                                    <span v-else>{{ (filterSelectedData || {}).selectedlocation.length > 0 ? (filterSelectedData || {}).selectedlocation[0].name : "NA" }}</span>
                                </div>
                            </div>
                            <div class="text-center px-4 py-2 hoverable">
                                <div class="grey--text text--darken-1 line-1">User Name</div>
                                <div class="font-weight-bold">
                                    <v-menu data-testid="bookclosure-project-list__user-menu" offset-y v-if="filterData.created_month.length > 1" open-on-hover>
                                        <template v-slot:activator="{ on, attrs }">
                                            <div data-testid="bookclosure-project-list__user-activator" class="filter-hide cursor-pointer" v-bind="attrs" v-on="on">
                                                <template v-if="(filterSelectedData || {}).selecteduser !== 'All'">
                                                    <span data-testid="bookclosure-project-list__user-value">{{ userdata[(filterSelectedData || {}).selecteduser].name || "" }}</span>
                                                </template>
                                                <template v-else>
                                                    <span data-testid="bookclosure-project-list__user-value-all">{{ (filterSelectedData || {}).selecteduser }}</span>
                                                </template>
                                                <v-icon data-testid="bookclosure-project-list__user-icon" class="">mdi-chevron-down</v-icon>
                                            </div>
                                        </template>
                                        <div data-testid="bookclosure-project-list__user-search-wrapper" @click.stop=";" class="white pa-2">
                                            <lb-string data-testid="bookclosure-project-list__user-search-input" v-model="userSearch" hidedetails label="Search"></lb-string>
                                        </div>
                                        <perfect-scrollbar data-testid="bookclosure-project-list__user-scrollbar" style="max-height: 300px">
                                            <v-list data-testid="bookclosure-project-list__user-list" dense class="pa-0 border-left-default">
                                                <v-list-item 
                                                    data-testid="bookclosure-project-list__user-item"
                                                    v-for="(v, k) in filteredUserList" 
                                                    :key="k"
                                                    @click="filterByUser(v)">
                                                    <v-list-item-title data-testid="bookclosure-project-list__user-item-title" v-if="v !== 'All'">{{ (userdata[v] || "NA").name || "NA" }}
                                                    </v-list-item-title>
                                                    <v-list-item-title data-testid="bookclosure-project-list__user-item-title-all" v-else>{{ (v || "NA") }}</v-list-item-title>
                                                </v-list-item>
                                            </v-list>
                                        </perfect-scrollbar>
                                    </v-menu>
                                    <span data-testid="bookclosure-project-list__user-static" v-else>{{ monthNames[(filterData || {}).created_month || "NA"] }}</span>
                                </div>
                            </div>
                            <div>
                                <lb-divider data-testid="bookclosure-project-list__filter-divider" type="vertical"></lb-divider>
                            </div>
                            <div data-testid="bookclosure-project-list__reset-filter-wrapper" class="text-center py-2 px-1 hoverable">
                                <div>
                                    <v-tooltip bottom content-class="tooltip-bottom">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn data-testid="bookclosure-project-list__reset-filter-btn" :class="{ 'primary': hasAnyActiveFilter }" :dark="hasAnyActiveFilter" icon v-bind="attrs" v-on="on" @click.stop="resetFilter()">
                                                <v-icon data-testid="bookclosure-project-list__reset-filter-icon">{{ hasAnyActiveFilter ? 'mdi-filter-remove' : 'mdi-close' }}</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Reset filters</span>
                                    </v-tooltip>
                                </div>
                            </div>
                        </div>
                        <v-btn data-testid="bookclosure-project-list__refresh-btn" class="shadow-off ml-1" icon @click="refreshData()" small>
                            <v-icon data-testid="bookclosure-project-list__refresh-icon">mdi-refresh</v-icon>
                        </v-btn>
                    </div>
                    <div data-testid="bookclosure-project-list__actions" class="d-flex  align-center justify-center">
                        <v-btn data-testid="bookclosure-project-list__add-cycle-btn" color="" class="mr-1 px-4 secondaryBtn" @click="addProjectDialogBox()" v-if="$nova.hasRight(right, 'add')">
                            <v-icon data-testid="bookclosure-project-list__add-cycle-icon" class="mr-1">mdi-plus</v-icon>
                            <span class="noTransform">Cycle</span>
                        </v-btn>
                        &nbsp;&nbsp;&nbsp;
                        <v-btn data-testid="bookclosure-project-list__add-ai-cycle-btn"  color="primary" class="mr-1 px-4" @click="addAIProjectDialogBox()" v-if="$nova.hasRight(right, 'add')">
                            <v-icon data-testid="bookclosure-project-list__add-ai-cycle-icon" class="mr-1">mdi-plus</v-icon>
                            <span class="noTransform">Gen AI Cycle</span>
                        </v-btn>
                    </div>
                </div>
                
                <div class="flex-fill-table" v-if="!initialLoading">
                    <lb-datatableaction ref="dataTable" data-testid="bookclosure-project-list__datatable" :headers="tableHeaders" :tabledata="formatedDataCopy" :displayedRowCount="paginatedItems.length" :itemsperpage="itemsPerPage" :bulkActions="false" @filter-item="filterPart" @clear-item="clearPart" :selectfilter="selectfilter" :page_details="page_details" :showClearFilter="false">
                        <template v-slot:tbody>
                            <tr data-testid="bookclosure-project-list__row" v-for="(item, index) in paginatedItems" :key="item._id" class="cursor-pointer" :class="{ 'low-opacity': item.status === false }">
                            <template v-for="header in tableHeaders">
                                <td data-testid="bookclosure-project-list__cell" v-if="header.visible && header.value !== 'action'" :key="header.value" @click="handleRowClick(item)" :class="{ 'description-header': header.value === 'description', 'grey--text text--darken-2': isCycleSettingUp(item.cycle_creation_status) && header.value !== 'stage' && header.value !== 'summary' }" class="px-2 single-line">
                                    <div data-testid="bookclosure-project-list__indexno-cell" v-if="header.value === 'indexno'">
                                        {{ (page - 1) * itemsPerPage + index + 1 }}
                                    </div>
                                    <div data-testid="bookclosure-project-list__stage-cell" v-else-if="header.value === 'stage'">

                                        <v-chip data-testid="bookclosure-project-list__stage-closed" small label v-if="item.stage === CYCLE_STAGES.CLOSED" class="green--text" color="lightgreen">{{ STAGE_LABELS[CYCLE_STAGES.CLOSED] }}</v-chip>
                                        <v-chip data-testid="bookclosure-project-list__stage-pending" small label text v-else-if="item.stage === CYCLE_STAGES.PENDING_REVIEW" class="yellow--text" color="lightyellow">{{ STAGE_LABELS[CYCLE_STAGES.PENDING_REVIEW] }}</v-chip>
                                        <v-chip data-testid="bookclosure-project-list__stage-open" small label text v-else-if="item.stage === CYCLE_STAGES.OPEN" class="blue--text" color="lightblue">{{ STAGE_LABELS[CYCLE_STAGES.OPEN] }}</v-chip>
                                    </div>
                                    <div data-testid="bookclosure-project-list__summary-cell" v-else-if="header.value === 'summary'">
                                        <template v-if="isCycleSettingUp(item.cycle_creation_status)">
                                            <v-skeleton-loader type="text" width="140" data-testid="bookclosure-project-list__summary-skeleton"></v-skeleton-loader>
                                        </template>
                                        <template v-else>
                                        <v-menu data-testid="bookclosure-project-list__summary-menu" offset-x left open-on-hover>
                                            <template v-slot:activator="{ on, attrs }">
                                                <div data-testid="bookclosure-project-list__summary-activator" class="my-1" v-bind="attrs" v-on="on">
                                                    <div data-testid="bookclosure-project-list__summary-header" class="font-weight-bold">
                                                        <v-tooltip bottom content-class="tooltip-bottom" v-if="item.managerlist.indexOf(userid) > -1">
                                                            <template v-slot:activator="{ on, attrs }">
                                                                <span v-bind="attrs" v-on="on"><v-avatar data-testid="bookclosure-project-list__manager-avatar" size="8" color="caccounting"></v-avatar></span>
                                                            </template>
                                                            <span>Manager role</span>
                                                        </v-tooltip>
                                                        <v-tooltip bottom content-class="tooltip-bottom" v-else>
                                                            <template v-slot:activator="{ on, attrs }">
                                                                <span v-bind="attrs" v-on="on"><v-avatar data-testid="bookclosure-project-list__user-avatar" size="8"
                                      color="grey lighten--2"></v-avatar></span>
                                                            </template>
                                                            <span>General user role</span>
                                                        </v-tooltip>
                                                        <span data-testid="bookclosure-project-list__summary-count" class="ml-1">
                                {{ (item.summary || {}).count || 0 }} Requests ({{ ((((item.summary || {}).approved || 0)
                                  /
                                  ((item.summary || {}).count || 0) || 0) * 100).toFixed(0) }}%)
                              </span>
                                                    </div>
                                                    <div data-testid="bookclosure-project-list__summary-progress" class="d-flex" style="width:100%; background-color:#e0e0e0; border-radius:2px;">
                                                        <div data-testid="bookclosure-project-list__summary-approved" class="green" :style="`height:10px; width: ${getProgressWidth(item.summary, 'approved')}%`">
                                                        </div>
                                                        <div data-testid="bookclosure-project-list__summary-pending-approval" class="yellow" :style="`height:10px; width: ${getProgressWidth(item.summary, 'pendingapproval')}%`">
                                                        </div>
                                                        <div data-testid="bookclosure-project-list__summary-pending" class="blue" :style="`height:10px; width: ${getProgressWidth(item.summary, 'pending')}%`">
                                                        </div>
                                                    </div>
                                                </div>
                                            </template>
                                            <v-card data-testid="bookclosure-project-list__summary-details-card" class="shadow-off">
                                                <v-card-text class="py-2">
                                                    <span data-testid="bookclosure-project-list__summary-details-title" class="">Progress details</span>
                                                    <div data-testid="bookclosure-project-list__summary-role">
                                                        <span>As</span>
                                                        <span data-testid="bookclosure-project-list__summary-role-manager" class="mx-1 caccounting--text font-weight-bold" v-if="item.managerlist.indexOf(userid) > -1">Manager</span>
                                                        <span data-testid="bookclosure-project-list__summary-role-user" class="mx-1 font-weight-bold" v-else>General User</span>
                                                        <span>role</span>
                                                    </div>
                                                </v-card-text>
                                                <v-divider></v-divider>
                                                <v-card-text class="py-2">
                                                    <table data-testid="bookclosure-project-list__summary-table">
                                                        <caption class="d-none">
                                                            Total Details
                                                        </caption>
                                                        <tbody>
                                                            <tr data-testid="bookclosure-project-list__summary-total">
                                                                <td class="font-weight-bold mr-2  pr-2">
                                                                    {{ (item.summary || {}).count || 0 }}
                                                                </td>
                                                                <td class="border-off">Total Task</td>
                                                            </tr>
                                                            <tr data-testid="bookclosure-project-list__summary-pending-row" class="info--text">
                                                                <td class="font-weight-bold mr-2 ">
                                                                    {{ (item.summary || {}).pending || 0 }}
                                                                </td>
                                                                <td class="border-off">Pending Submission</td>
                                                            </tr>
                                                            <tr data-testid="bookclosure-project-list__summary-pending-approval-row" class="warning--text">
                                                                <td class="font-weight-bold mr-2 ">
                                                                    {{ (item.summary || {}).pendingapproval || 0 }}
                                                                </td>
                                                                <td class="border-off">Pending Approval</td>
                                                            </tr>
                                                            <tr data-testid="bookclosure-project-list__summary-approved-row" class="success--text">
                                                                <td class="font-weight-bold mr-2 ">
                                                                    {{ (item.summary || {}).approved || 0 }}
                                                                </td>
                                                                <td class="border-off">Closed Task</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </v-card-text>
                                            </v-card>
                                        </v-menu>
                                        </template>
                                    </div>
                                    <div data-testid="bookclosure-project-list__location-cell" v-else-if="header.value === 'location'">
                                      <div data-testid="bookclosure-project-list__location-content" v-if="item.location && item.location.length">
                                        <span data-testid="bookclosure-project-list__location-primary" v-if="getLocationById(item.location[0])"
                                          class="font-small caccounting--text caccounting-background pa-1 mr-2 radius-mini">
                                          {{ getLocationById(item.location[0]).name }}
                                        </span>
                                        <span data-testid="bookclosure-project-list__location-primary-fallback" v-else
                                          class="font-small caccounting--text caccounting-background pa-1 mr-2 radius-mini">
                                          {{ item.location[0] }}
                                        </span>

                                        <v-tooltip data-testid="bookclosure-project-list__location-more-tooltip" top content-class="tooltip-top" v-if="item.location.length > 1">
                                          <template v-slot:activator="{ on }">
                                            <span data-testid="bookclosure-project-list__location-more-badge" v-on="on"
                                              class="border-on radius-mini text-caption purple--text pa-1">
                                              +{{ item.location.length - 1 }}
                                            </span>
                                          </template>
                                          <div data-testid="bookclosure-project-list__location-more-list">
                                            <div data-testid="bookclosure-project-list__location-item" v-for="(loc, index) in item.location.slice(1)" :key="index">
                                              <span data-testid="bookclosure-project-list__location-item-name">{{ getLocationById(loc) ? getLocationById(loc).name : loc }}</span>
                                            </div>
                                          </div>
                                        </v-tooltip>
                                      </div>
                                    </div>
                                    <div class="" v-else-if="header.value ==='created_at' || header.value === 'updated_at' || header.value==='to' || header.value==='from'">
                                        {{ $nova.formatDate(getItemData(item, header.value)) }}
                                    </div>
                                    <div v-else-if="header.value === 'description'" class="d-flex align-center" style="min-width:0;" data-testid="bookclosure-project-list__description-cell">
                                        <span class="text-truncate flex-shrink-1" style="min-width:0;">{{ item.description }}</span>
                                        <v-chip v-if="item.cycle_creation_status && item.cycle_creation_status !== CYCLE_CREATION_STATUS.COMPLETED && item.cycle_creation_status !== CYCLE_CREATION_STATUS.PARTIALLY_COMPLETED"
                                            x-small label class="ml-2 flex-shrink-0 cycle-status-badge"
                                            :color="getCycleStatusConfig(item.cycle_creation_status).bgColor"
                                            :class="getCycleStatusConfig(item.cycle_creation_status).textClass"
                                            data-testid="bookclosure-project-list__cycle-status-badge">
                                            <v-icon x-small class="mr-1" :class="getCycleStatusConfig(item.cycle_creation_status).textClass">{{ getCycleStatusConfig(item.cycle_creation_status).icon }}</v-icon>
                                            {{ getCycleStatusConfig(item.cycle_creation_status).label }}
                                        </v-chip>
                                    </div>
                                    <span data-testid="bookclosure-project-list__cell-value" v-else> {{ getItemData(item, header.value) }}</span>
                                </td>
                            </template>
                            
                            <td data-testid="bookclosure-project-list__action-cell" class="  d-flex align-center justify-center ">
                                <!-- Skeleton when cycle creation is pending or in_progress -->
                                <template v-if="isCycleSettingUp(item.cycle_creation_status)">
                                    <v-skeleton-loader type="text" width="80" data-testid="bookclosure-project-list__action-skeleton"></v-skeleton-loader>
                                </template>

                                <!-- Error state when cycle creation failed -->
                                <template v-else-if="item.cycle_creation_status === CYCLE_CREATION_STATUS.FAILED">
                                    <CycleErrorDetail
                                      :error-message="item.cycle_creation_error_message"
                                      data-testid="bookclosure-project-list__cycle-error-detail"
                                    />
                                    <v-tooltip bottom content-class="tooltip-bottom">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn data-testid="bookclosure-project-list__retry-btn" class="mr-1" @click.stop="retryCycleCreation(item._id)" v-bind="attrs" v-on="on" icon small>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                                                    <path d="M12.8125 3.4375H5.9375C3.6167 3.4375 1.875 5.11564 1.875 7.5" stroke="#797E89" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                                    <path d="M2.1875 11.5625H9.0625C11.3833 11.5625 13.125 9.88438 13.125 7.5" stroke="#797E89" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                                    <path d="M11.5625 1.875C11.5625 1.875 13.125 3.02576 13.125 3.43751C13.125 3.84926 11.5625 5 11.5625 5" stroke="#797E89" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                                    <path d="M3.43749 10C3.43749 10 1.87501 11.1507 1.875 11.5625C1.87499 11.9743 3.4375 13.125 3.4375 13.125" stroke="#797E89" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                                </svg>
                                            </v-btn>
                                        </template>
                                        <span>Retry Setup</span>
                                    </v-tooltip>
                                    <v-tooltip bottom content-class="tooltip-bottom" v-if="item.managerlist.indexOf(userid) > -1">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn data-testid="bookclosure-project-list__delete-btn" class="mr-1" @click.stop="deleteProject(item._id)" v-bind="attrs" v-on="on" icon small color="error">
                                                <v-icon data-testid="bookclosure-project-list__delete-icon">mdi-delete</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Delete Cycle</span>
                                    </v-tooltip>
                                </template>

                                <!-- Normal state (completed, partially_completed, or default) -->
                                <template v-else>
                                <v-tooltip bottom content-class="tooltip-bottom" v-if="item.stage !== CYCLE_STAGES.CLOSED && item.managerlist.indexOf(userid) > -1 && $nova.hasRight(right, 'edit')">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn data-testid="bookclosure-project-list__edit-btn" class="mr-1 " @click.stop="addeditproject = { ...item }; editprojectdialog = true; addeditprojecterror = {};" v-bind="attrs" v-on="on" icon small>
                                            <v-icon data-testid="bookclosure-project-list__edit-icon" class="">mdi-pencil</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Edit</span>
                                </v-tooltip>
                                <v-tooltip bottom content-class="tooltip-bottom" v-if="item.stage === CYCLE_STAGES.CLOSED && item.managerlist.indexOf(userid) > -1 && $nova.hasRight(right, 'open')">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn data-testid="bookclosure-project-list__reopen-btn" class="mr-1" @click.stop="openProject(item.indexno - 1, item._id)" v-bind="attrs" v-on="on" icon small>
                                            <v-icon data-testid="bookclosure-project-list__reopen-icon" class="">mdi-circle-outline</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Re-Open</span>
                                </v-tooltip>
                                <v-tooltip bottom content-class="tooltip-bottom" v-else-if="item.managerlist.indexOf(userid) > -1 && $nova.hasRight(right, 'close')">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn data-testid="bookclosure-project-list__close-btn" class="mr-1" @click.stop="closeProject(item.indexno - 1, item._id)" v-bind="attrs" v-on="on" icon small>
                                            <v-icon data-testid="bookclosure-project-list__close-icon" class="">mdi-circle</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Close</span>
                                </v-tooltip>
                                 <activity-log
                                  :resource-id="item._id"
                                  :row-description="item.description"
                                  data-testid="bookclosure-project-list__activity-log-btn"
                                />
                                <v-tooltip bottom content-class="tooltip-bottom" v-if="item.notification && item.managerlist.indexOf(userid) > -1">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn data-testid="bookclosure-project-list__disable-notification-btn" class="mr-1" @click.stop="diableenableNotification(item._id,'Disable')" v-bind="attrs" v-on="on" icon small>
                                            <v-icon data-testid="bookclosure-project-list__disable-notification-icon" class="">mdi-email</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Email Notifications Disable</span>
                                </v-tooltip>

                                 <v-tooltip bottom content-class="tooltip-bottom" v-else>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn data-testid="bookclosure-project-list__enable-notification-btn" class="mr-1" @click.stop="diableenableNotification(item._id,'Enable')" v-bind="attrs" v-on="on" icon small>
                                            <v-icon data-testid="bookclosure-project-list__enable-notification-icon" class="">mdi-email-off</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Email Notifications Enable</span>
                                </v-tooltip>
                                </template>
                            </td>
                        </tr>
                        </template>
                    </lb-datatableaction>
                </div>
            </v-card-text>
        </v-card>

        <lb-drawer data-testid="bookclosure-project-list__add-cycle-drawer" v-model="addprojectdialog" heading="Create New Cycle" width="600" :loading="addprojectloading">
            <template v-slot:body>
                <div data-testid="bookclosure-project-list__add-cycle-form">
                    <!-- Basic Project Information Section -->
                    <div data-testid="bookclosure-project-list__basic-info-section" class="mb-6">
                        <h4 class="mb-4 font-weight-regular">Basic Cycle Information:</h4>
                        <v-row class="">
                            <v-col cols="6" class="my-0 py-0">
                                <lb-date data-testid="bookclosure-project-list__period-from-input" v-model="addeditproject.from" label="Period From*" :error="addeditprojecterror.from" />
                            </v-col>
                            <v-col cols="6" class="my-0 py-0">
                                <lb-date data-testid="bookclosure-project-list__period-to-input" v-model="addeditproject.to" label="Period To*" :error="addeditprojecterror.to" :min="addeditproject.from" />
                            </v-col>
                            <v-col cols="12" class="my-0 py-0">
                                <lb-string data-testid="bookclosure-project-list__description-input" v-model="addeditproject.description" label="Description*" :error="addeditprojecterror.description" />
                            </v-col>
                            <v-col cols="12" class="my-0 py-0">
                                <v-checkbox data-testid="bookclosure-project-list__notification-checkbox" label="Do you want automatic email notifications?" class="mt-0 mr-4 info--text" dense hide-details color="info" v-model="addeditproject.notification"></v-checkbox> 
                            </v-col>
                            <!-- <v-col cols="12" class="my-0 py-1">
                      <lb-dropdown label="Associate User(s)" multiple v-model="addeditproject.userlist" :error="addeditprojecterror.userlist" :items="formdata.users" itemtext="name" itemvalue="_id" />
                    </v-col> -->
                        </v-row>
                    </div>

                    <!-- Tasklist Configuration Section -->
                   <div data-testid="bookclosure-project-list__tasklist-config-section" class="mb-4">
                        <h4 class="mb-4 font-weight-regular">Tasklist Configuration:</h4>
                        <lb-dropdown data-testid="bookclosure-project-list__populate-checklist-dropdown" label="Populate Checklist*" v-model="checklistfrom" :error="addeditprojecterror.checklistfrom" :items="populatechecklistitems" itemtext="name" itemvalue="_id"/>
                        <lb-dropdown data-testid="bookclosure-project-list__select-project-dropdown" v-if="checklistfrom === 'previousproject'" label="Select Project*" v-model="checklistfromvalue" :items="formatedDataCopy" itemtext="description" itemvalue="_id" :error="addeditprojecterror.project"/>
                        <lb-dropdown data-testid="bookclosure-project-list__location-dropdown" v-if="checklistfrom === 'checklistmaster' || checklistfrom === 'blank'" label="Location*" v-model="selectedCycleLocations" :items="LocationsList"  itemtext="name" itemvalue="_id" multiple :error="addeditprojecterror.location" />
                        <lb-dropdown data-testid="bookclosure-project-list__cycle-type-dropdown" v-if="checklistfrom === 'checklistmaster'" label="Select Cycle Type*" v-model="selectedCycleType" :items="cycleTypes" itemtext="displayText" itemvalue="name" :error="addeditprojecterror.cycleType" item-class="noTransform" />
                        <div data-testid="bookclosure-project-list__tags-section" v-if="checklistfrom === 'checklistmaster'" class="mx-2">
                            <div data-testid="bookclosure-project-list__tags-header" class="mb-1 d-flex">
                                <div data-testid="bookclosure-project-list__tags-label" class="font-weight-bold">Selects the tags*:</div>
                                <v-spacer></v-spacer>
                                <div data-testid="bookclosure-project-list__tags-actions" v-if="(formdata.tags || []).length > 1" class="caption">
                                    <v-btn data-testid="bookclosure-project-list__select-all-tags-btn" x-small text class="mr-1" @click="checklistfromvalue = formdata.tags">
                                        <v-icon class="mr-1">mdi-checkbox-multiple-marked</v-icon><span>Select all</span></v-btn>
                                    <v-btn data-testid="bookclosure-project-list__deselect-all-tags-btn" x-small text @click="checklistfromvalue = []">
                                        <v-icon class="mr-1">mdi-checkbox-multiple-blank-outline</v-icon><span>Deselect all</span></v-btn>
                                </div>
                            </div>
                            <template v-if="(formdata.tags || []).length > 0">
                                <v-row data-testid="bookclosure-project-list__tags-list" class="mt-2">
                                    <v-col 
                                        data-testid="bookclosure-project-list__tag-item"
                                        v-for="(v, k) in formdata.tags || []" 
                                        :key="k" 
                                        cols="12" 
                                        sm="6" 
                                        md="4" 
                                        class="py-1"
                                    >
                                        <v-checkbox 
                                            data-testid="bookclosure-project-list__tag-checkbox"
                                            v-model="checklistfromvalue" 
                                            :value="v" 
                                            :label="v"
                                            multiple
                                            dense
                                            hide-details
                                            color="primary"
                                            class="ma-0"
                                        ></v-checkbox>
                                    </v-col>
                                </v-row>
                            </template>
                            <div data-testid="bookclosure-project-list__no-tags" v-else class="grey--text">No tags found</div>
                            <div v-if="addeditprojecterror.tags" class="v-messages error--text mt-2" role="alert" data-testid="bookclosure-project-list__tags-error">
                                <div class="v-messages__wrapper">
                                    <div class="v-messages__message">{{ addeditprojecterror.tags }}</div>
                                </div>
                            </div>
                        </div>
                        <div data-testid="bookclosure-project-list__master-note" v-if="checklistfrom === 'checklistmaster'" class="mt-4">
                            <div class="font-weight-bold mr-2">Note:</div>
                            <div>
                                The Users associated in the masters will be added as general users to this cycle.
                            </div>
                            <div>
                                The Information Owners associated in the Information Manager master will also be added to this cycle.
                            </div>
                        </div>
                        <div data-testid="bookclosure-project-list__previous-project-note" v-else-if="checklistfrom === 'previousproject'">
                            <div data-testid="bookclosure-project-list__previous-project-note-title" class="font-weight-bold mr-2">Note:</div>
                            <div data-testid="bookclosure-project-list__previous-project-note-content">
                                The Users/Information Owners associated in the selected previous project will be added to this project.
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template v-slot:actions>
                <span class="caption">*Required</span>
                <v-spacer></v-spacer>
                <v-btn data-testid="bookclosure-project-list__create-cycle-btn" color="primary" v-if="addeditproject.from && addeditproject.to && addeditproject.description && addeditproject.description.trim() !== ''" @click="addProject()"><span>Create</span></v-btn>
            </template>
        </lb-drawer>

        <lb-drawer data-testid="bookclosure-project-list__add-ai-cycle-drawer" v-model="addAIProjectDialog" heading="Create New AI Cycle" width="600" :loading="addprojectloading">
            <template v-slot:body>
                <div data-testid="bookclosure-project-list__add-ai-cycle-form">
                    <!-- Basic Project Information Section -->
                    <div data-testid="bookclosure-project-list__ai-basic-info-section" class="mb-6">
                        <h4 class="mb-4 font-weight-regular">Basic Project Information</h4>
                        <v-row>
                            <v-col cols="6" class="my-0 py-1">
                                <lb-date data-testid="bookclosure-project-list__ai-period-from-input" v-model="addeditproject.from" label="Period From*" :error="addeditprojecterror.from" />
                            </v-col>
                            <v-col cols="6" class="my-0 py-1">
                                <lb-date data-testid="bookclosure-project-list__ai-period-to-input" v-model="addeditproject.to" label="Period To*" :error="addeditprojecterror.to" :min="addeditproject.from" />
                            </v-col>
                            <v-col cols="12" class="my-0 py-1">
                                <lb-string data-testid="bookclosure-project-list__ai-description-input" v-model="addeditproject.description" label="Description*" :error="addeditprojecterror.description" />
                            </v-col>
                        </v-row>
                    </div>

                    <!-- Tasklist Configuration Section -->
                    <div data-testid="bookclosure-project-list__ai-tasklist-config-section" class="mb-4">
                        <h4 class="mb-4 font-weight-regular">Tasklist Configuration</h4>
                        <lb-dropdown data-testid="bookclosure-project-list__ai-populate-checklist-dropdown" label="Populate Checklist*" v-model="checklistfrom" :error="addeditprojecterror.checklistfrom" :items="populateAIchecklistitems" itemtext="name" itemvalue="_id" :readonly='true' :clearable='false' />
                    </div>
                </div>
            </template>
            <template v-slot:actions>
                <span class="caption">*Required</span>
                <v-spacer></v-spacer>
                <v-btn data-testid="bookclosure-project-list__create-ai-cycle-btn" small color="primary" v-if="checklistfrom === 'genAI' && addeditproject.from && addeditproject.to && addeditproject.description && addeditproject.description.trim() !== ''" @click="createCheckListUsingGenAI()"><span>Create</span></v-btn>
            </template>
        </lb-drawer>

        <lb-drawer data-testid="bookclosure-project-list__edit-cycle-drawer" v-model="editprojectdialog" heading="Edit Cycle" max-width="600" persistent>
            <template v-slot:body>
                <v-row>
                    <v-col cols="6" class="my-0 py-1">
                        <p class="pa-0 ma-0 gray--text mb-1">Period From*</p>
                        <lb-date data-testid="bookclosure-project-list__edit-period-from-input" v-model="addeditproject.from" label="" :error="addeditprojecterror.from"    placeholder="Start Date" />
                    </v-col>
                    <v-col cols="6" class="my-0 py-1">
                        <p class="pa-0 ma-0 gray--text mb-1">Period To*</p>
                        <lb-date data-testid="bookclosure-project-list__edit-period-to-input" v-model="addeditproject.to" label="" :error="addeditprojecterror.to" :min="addeditproject.from"  placeholder="End Date" />
                    </v-col>

                    <v-col cols="12" class="my-0 py-1">
                        <p class="pa-0 ma-0 gray--text mb-1">Description*</p>
                        <lb-string data-testid="bookclosure-project-list__edit-description-input" v-model="addeditproject.description" label="" :error="addeditprojecterror.description" />
                    </v-col>
                </v-row>

            </template>
            <template v-slot:actions>
                <v-spacer></v-spacer>
                <v-btn data-testid="bookclosure-project-list__update-cycle-btn" color="primary" @click="editProject()"><span>Update</span></v-btn>
            </template>

        </lb-drawer>

        <lb-drawer data-testid="bookclosure-project-list__gen-ai-data-drawer" v-model="displayGenAIDataDialog" heading="Generated List" width="600" persistent>
            <template v-slot:body>
                <div>
                    <div data-testid="bookclosure-project-list__gen-ai-table-wrapper" class="align-center mb-3 mt-3">
                        <v-simple-table data-testid="bookclosure-project-list__gen-ai-table" dense v-if="genAIData.length > 0">
                            <thead>
                                <tr data-testid="bookclosure-project-list__gen-ai-table-header">
                                    <th class="text-left">Description</th>
                                    <th class="text-left">Category</th>
                                    <th class="text-left">Sub category</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr data-testid="bookclosure-project-list__gen-ai-table-row" v-for="(v, k) in genAIData" :key="k">
                                    <td data-testid="bookclosure-project-list__gen-ai-description">{{ v.description }}</td>
                                    <td data-testid="bookclosure-project-list__gen-ai-category">{{ v.category }}</td>
                                    <td data-testid="bookclosure-project-list__gen-ai-subcategory">{{ v.subcategory }}</td>
                                </tr>
                            </tbody>
                        </v-simple-table>
                    </div>
                    <v-divider></v-divider>
                </div>
            </template>
            <template v-slot:actions>
                <v-spacer></v-spacer>
                <v-btn data-testid="bookclosure-project-list__gen-ai-create-btn" small class="ml-2" color="primary" @click="addProject()"><span>Create</span></v-btn>
            </template>
        </lb-drawer>

        <!-- Pagination -->
        <Pagination 
            v-if="!initialLoading"
            :list="filteredData" 
            :itemsperpage="itemsPerPage" 
            @paginated-data="setPaginatedData" 
            data-testid="bookclosure-project-list__pagination" 
        />
    </div>
</template>

<script>
import { CYCLE_MESSAGES, CYCLE_CREATION_STATUS, CYCLE_STAGES, STAGE_LABELS, FC_ROUTES } from '../utils/constants';
import constants from '../../../plugins/constants';
import Pagination from '../components/Pagination/Pagination.vue';
import ActivityLog from '../components/ActivityLog/ActivityLog.vue';
import CycleErrorDetail from '../components/CycleErrorDetail/CycleErrorDetail.vue';

  export default {
        name: "bookclosure_project_list",
        // props: ['props'],
        components: {
          Pagination,
         ActivityLog,
         CycleErrorDetail
        },
        data: function () {
          return {
            CYCLE_STAGES,
            STAGE_LABELS,
            right: "financialclose_project",
            userid: "",
            generateddescription: "",
            addeditproject: {},
            addeditprojecterror: {},
            addprojectdialog: false,
            addAIProjectDialog: false,
            checklistfrom: "",
            checklistfromvalue: null,
            formdata: {},
            populatechecklistitems: [
              { _id: "blank", name: "Blank" },
              { _id: "checklistmaster", name: "Tasklist Master" },
              { _id: "previousproject", name: "Previous Project" },
            ],
            populateAIchecklistitems: [
              { _id: "genAI", name: "Generative AI" },
            ],
            addprojectloading: false,
            editprojectdialog: false,
            editprojectloading: false,
            loading: false,
            initialLoading: false, // For skeleton loader during initial data fetch
            listdata: [],
            activitylogconfig: {
              userassociate: {
                icon: "mdi-plus",
                color: "info",
                displaytext: "User Associated",
              },
              userreplaced: {
                icon: "mdi-account-convert",
                color: "info",
                displaytext: "User Replaced",
              },
              userremoved: {
                icon: "mdi-close",
                color: "error",
                displaytext: "User Removed",
              },
              usermoved: { icon: "mdi-sync", color: "", displaytext: "Updated" },
              projectcreate: {
                icon: "mdi-plus",
                color: "info",
                displaytext: "Created",
              },
              projectopen: {
                icon: "mdi-lock-open-variant",
                color: "info",
                displaytext: "Opened",
              },
              projectclose: {
                icon: "mdi-lock",
                color: "success",
                displaytext: "Closed",
              },
              projectupdated: {
                icon: "mdi-pencil",
                color: "primary",
                displaytext: "Updated",
              },
              tbadd: {
                icon: "mdi-table-plus",
                color: "error",
                displaytext: "Trial Balance Added",
              },
              bulkpbcadd: {
                icon: "mdi-file-question-outline",
                color: "",
                displaytext: "Information Request Added",
              },
            },
            userdata: {},
      
            userlist: [],
            managerlist: [],
      
            tableHeaders: [
              {
                text: 'SL No',
                value: 'indexno',
                visible: true,
                filterable: false,
                sortable: false,
              },
              {
                value: "description",
                text: "Description",
                sortable: false,
                filterable: true,
                visible: true,
              },
              {
                value: "from",
                text: "Start Period",
                sortable: false,
                filterable: true,
                visible: true,
              },
              {
                value: "to",
                text: "End Period",
                sortable: false,
                filterable: true,
                visible: true,
              },
              {
                value: "stage",
                text: "Status",
                sortable: false,
                filterable: false,
                visible: false,
              },
               {
                value: "location",
                text: "Locations",
                sortable: false,
                filterable: false,
                visible: true,
              },
              {
                value: "summary",
                text: "Tasklist Progress",
                sortable: false,
                filterable: false,
                visible: true,
              },
      
              {
                value: "created_at",
                text: "Created At",
                sortable: false,
                filterable: false,
                visible: false,
              },
              {
                value: "updated_at",
                text: "Updated At",
                sortable: false,
                filterable: false,
                visible: false,
              },
              {
                value: "action",
                text: "Action",
                sortable: false,
                filterable: false,
                visible: true,
              },
            ],
            formatedData: [],
            formatedDataCopy: [],
            displayGenAIDataDialog: false,
            genAIData: [],
            genAITableHeaders: [
              {
                value: "description",
                text: "Descritption",
              },
              {
                value: "category",
                text: "Category",
              },
              {
                value: "subcategory",
                text: "Sub Category",
              }
            ],
            isFirst: false,
            isPromptVisible: false,
            promptMessage: "This will help in linking your finance close task list with your Chart of Accounts and Trial Balance.",
            promptStyles: {
              top: "0",
              left: "0"
            },
            filterData: { created_year: [], created_month: [], userlist: [], location: [] },
            filterSelectedData: { "selectedyear": "All", "selectedmonth": "All", "selecteduser": "All","selectedlocation": [] },
            timeFormat: {
              month: 'short',   // Short month name (e.g., Dec)
              day: 'numeric',   // Day of the month as a numeric value (e.g., 28)
              year: 'numeric',  // Full year (e.g., 2023)
              hour: 'numeric',  // Hour (e.g., 16)
              minute: 'numeric'
            },
            monthNames: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
            filteredData: [],
            selectedCycleType: "FBC",
            selectedCycleLocations:[],
            customColumns:[],
            allCustomColumns:{},
            cycleTypes:[
              {
                displayText:"FBC",
                name:"FBC"
              },
              {
                displayText:"RM",
                name:"RM"
              },
              {
                displayText:"IA",
                name:"IA"
              },
              {
                displayText:"Others",
                name:"OTHERS"
              },
            ],
            sectorsList: [],
            activeUserIds: [],
            userSearch: "",
            itemsPerPage: 14,
            search: '',
            page: 1,
            paginatedItems: [],
            countrylist:[],
            LocationsList:[],
            selectfilter: {},
            page_details: {},
            pollingTimer: null,
            CYCLE_CREATION_STATUS,
          };
        },
        created() {
          this.init();
        },
        activated() {
          this.init();
        },
        methods: {
          async getLocation() {
            // V3: Locations are fetched via tasks/fetchFormDataV3 in refreshData.
            // This method is kept for backward compatibility but is now a no-op.
        },
          handleRowClick(item) {
            localStorage.setItem(constants.STORAGE_KEYS.USER_OBJ, JSON.stringify(item));
            this.$store.commit('addItemToCycleList', item);
            this.$nova.gotoLink({ path: FC_ROUTES.CHECKLIST(item._id) });
          },
          setPaginatedData(paginationInfo) {
            // Handle both object format {data, page} and direct array
            if (paginationInfo && typeof paginationInfo === 'object' && 'data' in paginationInfo) {
              this.paginatedItems = paginationInfo.data || [];
              this.page = paginationInfo.page || 1;
            } else {
              this.paginatedItems = paginationInfo || [];
            }
          },
          getItemData(item, key) {
            return item[key];
          },
          async getActiveUsers(){
            // V3: Active users are fetched via tasks/fetchFormDataV3 in refreshData.
            // This method is kept for backward compatibility but is now a no-op.
          },
          async isChartOfAccountsSetup() {
            // V3: hasChartOfAccounts is fetched via tasks/fetchFormDataV3 in refreshData.
            // This method is kept for backward compatibility but is now a no-op.
          },
          formateData() {
            let ObjectTemp = {};
            let ArrayTemp = [];
            this.filterData = { "created_year": new Set(), "created_month": new Set(), "userlist": new Set() };
            this.listdata.forEach((item, index) => {
              let date = new Date(item.from)
              this.filterData.created_year.add(date.getFullYear())
              this.filterData.created_month.add(this.monthNames[date.getMonth()])
              item.managerlist?.filter((user) => this.filterData.userlist.add(user))
              item.userlist?.filter((user) => this.filterData.userlist.add(user))
              ObjectTemp = {
                __key: index,
                __formatedkey: index,
                _id: item._id,
                indexno: index + 1,
                __click: FC_ROUTES.CHECKLIST(item._id),
                type: item.type,
                category: item.category,
                description: item.description,
                files: item.files,
                nopbc: item.nopbc,
                notification:item.notification,
                pbcproject: item.pbcproject,
                from: item.from?.split("T")[0],
                to: item.to?.split("T")[0],
                stage: item.stage,
                summary: item.summary,
                created_at: item.created_at?.split("T")[0],
                updated_at: item.updated_at?.split("T")[0],
                year: date.getFullYear(),
                month: this.monthNames[date.getMonth()],
                status: item.status,
                managerlist: item.managerlist || [],
                conversation_read: item.conversation_read,
                userlist: item.userlist || [],
                location: item.location || [],
                cycle_creation_status: item.cycle_creation_status || CYCLE_CREATION_STATUS.COMPLETED,
                cycle_creation_error_message: item.cycle_creation_error_message || null,
              };
              ArrayTemp.push(ObjectTemp);
            });
            this.formatedData = ArrayTemp;
            this.formatedDataCopy = ArrayTemp;
            this.filteredData = this.formatedData;
            this.filterData.created_year = Array.from(this.filterData.created_year);
            this.filterData.created_year.push('All');
            this.filterData.created_month = Array.from(this.filterData.created_month);
            this.filterData.created_month.push('All');
            this.filterData.userlist = Array.from(this.filterData.userlist);
            this.filterData.userlist.push('All');
            this.filterData.location = Array.from(this.LocationsList);
            this.filterData.location.push('All');
          },
          init() {
            this.userid = this.$store.state.user.id;
            this.refreshData();
          },
          async refreshData() {
            this.charts = [];
            this.chartdata = {};
            this.listdata = [];
            this.resetFilter();

            // V3: Fetch formdata (locations + users + hasChartOfAccounts) + project list in parallel
            this.loading = true;
            this.initialLoading = true;
            try {
              const formDataV3 = await this.$store.dispatch('tasks/fetchFormDataV3');
              if (formDataV3) {
                // Populate locations
                this.LocationsList = (formDataV3.locations || []).map(loc => ({
                  _id: loc._id,
                  name: loc.name,
                  country: loc.country,
                  type: loc.type,
                }));

                // Populate active users
                this.activeUserIds = (formDataV3.users || []).filter(u => u.isActive).map(u => u._id);

                // hasChartOfAccounts check
                this.isFirst = !formDataV3.hasChartOfAccounts;

                // Populate formdata users for project creation dialog
                this.formdata = {
                  users: (formDataV3.users || []).map(u => ({
                    _id: u._id,
                    name: u.name,
                    email: u.email,
                    displayname: u.name + '(' + u.email + ')',
                  })),
                  tags: formDataV3.tags || [],
                };
                // Add current user if not already present
                const currentUserId = this.$store.state.user.id;
                if (!this.formdata.users.find(u => u._id === currentUserId)) {
                  this.formdata.users.push({
                    name: this.$store.state.user.name,
                    _id: currentUserId,
                    displayname: this.$store.state.user.name + '(' + this.$store.state.user.email + ')',
                    email: this.$store.state.user.email,
                  });
                }
                for (const i of this.formdata.users || []) {
                  this.userdata[i._id] = i;
                }
              }

              // Fetch project list
              await this.getProjectsData();
            } catch (err) {
              console.error('[Projects]: refreshData error', err);
              this.$store.commit("sbError", err.message || "Unknown error!");
            } finally {
              this.formateData();
              this.loading = false;
              this.initialLoading = false;
              this._schedulePollingIfNeeded();
            }
          },
          addProjectDialogBox() {
            this.addeditproject = { 'description': undefined, 'from': undefined, 'to': undefined };
            this.checklistfrom = "checklistmaster";
            this.selectedCycleType = "FBC";
            this.selectedCycleLocations = [];
            this.checklistfromvalue = null;
            this.addeditprojecterror = {};
            this.managerlist = [this.$store.state.user.id];
            this.addprojectdialog = true;
          },
          addAIProjectDialogBox() {
            this.addeditproject = { 'description': undefined, 'from': undefined, 'to': undefined };
            this.checklistfrom = "genAI";
            this.checklistfromvalue = null;
            this.addeditprojecterror = {};
            this.managerlist = [this.$store.state.user.id];
            this.addAIProjectDialog = true;
          },
          _schedulePollingIfNeeded() {
            const needsPoll = this.listdata.some(
              item => item.cycle_creation_status === CYCLE_CREATION_STATUS.PENDING ||
                      item.cycle_creation_status === CYCLE_CREATION_STATUS.IN_PROGRESS
            );
            if (needsPoll && !this.pollingTimer) {
              this.pollingTimer = setTimeout(async () => {
                this.pollingTimer = null;
                await this.getProjectsData();
                this.formateData();
                this._schedulePollingIfNeeded();
              }, 45000);
            } else if (!needsPoll && this.pollingTimer) {
              clearTimeout(this.pollingTimer);
              this.pollingTimer = null;
            }
          },
         async getProjectsData() {
           await this.$store.dispatch('project/fetchProjectListV3');
            this.listdata = this.$store.getters['project/projectList'];
          },
          async createCheckListUsingGenAI() {
            this.addprojectloading = true;
            try {
              const dt = await this.$store.dispatch('project/generateAIChecklist', this.addeditproject);
              if (dt.data.status === "success") {
                this.genAIData = dt.data.data[0].data;
                this.addeditproject["data"] = dt.data.data[0].data;
                this.displayGenAIDataDialog = true;
              } else {
                if (dt.data.data.length > 0) {
                  if (dt.data.data[0].index0)
                    this.addeditprojecterror = dt.data.data[0].index0;
                  this.$store.commit('sbError', dt.data.message || CYCLE_MESSAGES.ERROR.UNKNOWN);
                } else throw new Error(dt.data.message);
              }
            } catch (err) {
              console.log('[Projects]: createCheckListUsingGenAI error', err);
            } finally {
              this.addprojectloading = false;
            }
          },
          async addProject() {
            // Validate checklist selection
            if (!this.checklistfrom) {
              this.$store.commit("sbError", CYCLE_MESSAGES.ERROR.SELECT_POPULATE_CHECKLIST);
              return;
            }

            // Validate project is selected when "Previous Project" is chosen
            if (this.checklistfrom === 'previousproject' && (!this.checklistfromvalue || typeof this.checklistfromvalue !== 'string')) {
              this.$set(this.addeditprojecterror, 'project', CYCLE_MESSAGES.ERROR.NO_PROJECT_SELECTED);
              return;
            }

            // Validate cycle type is selected when "Tasklist Master" is chosen
            if (this.checklistfrom === 'checklistmaster' && (!this.selectedCycleType || this.selectedCycleType === "")) {
              this.$set(this.addeditprojecterror, 'cycleType', CYCLE_MESSAGES.ERROR.PLEASE_SELECT_CYCLE_TYPE);
              return;
            }

            // Validate locations are selected when "Tasklist Master" or "Blank" is chosen
            if ((this.checklistfrom === 'checklistmaster' || this.checklistfrom === 'blank') && (!this.selectedCycleLocations || this.selectedCycleLocations.length === 0)) {
              this.$set(this.addeditprojecterror, 'location', CYCLE_MESSAGES.ERROR.PLEASE_SELECT_CYCLE_LOCATIONS);
              return;
            }

            // Validate at least one tag is selected when "Tasklist Master" is chosen
            if (this.checklistfrom === 'checklistmaster' && (!Array.isArray(this.checklistfromvalue) || this.checklistfromvalue.length === 0)) {
              this.$set(this.addeditprojecterror, 'tags', CYCLE_MESSAGES.ERROR.NO_TAGS_SELECTED);
              return;
            }

            this.displayGenAIDataDialog = false;
            this.addprojectloading = true;

            try {
              // Prepare payload
              this.addeditproject.checklistfrom = this.checklistfrom;
              if (this.checklistfrom === 'checklistmaster') {
                this.addeditproject.cycle_type = this.selectedCycleType;
                this.addeditproject.location = this.selectedCycleLocations;
              } else if (this.checklistfrom === 'blank') {
                this.addeditproject.location = this.selectedCycleLocations;
              }

              // Dispatch Vuex action for adding project
              const dt = await this.$store.dispatch('project/addProject', this.addeditproject);

              if (dt?.data?.status === "success") {
                const newProjectId = dt.data.project?._id;
                const newProjectDescription = dt.data.project?.description || this.addeditproject.description;
                
                // Sync with ERP if enabled
                if (this.$store.state.erpEnabled.tbErpEnabled && newProjectId) {
                  await this.$store.dispatch('project/syncERP', { payload: this.addeditproject, id: newProjectId });
                }

                this.$store.commit("sbSuccess", dt.data.message || CYCLE_MESSAGES.SUCCESS.NEW_CYCLE_CREATED);

                if (newProjectId) {
                  this.$store.commit('addItemToCycleList', {
                    _id: newProjectId,
                    description: newProjectDescription
                  });
                }
                this.addprojectdialog = false;
                this.addAIProjectDialog = false;
                this.refreshData();
              } else {
                // Handle error response
                if (Array.isArray(dt?.data?.data) && dt.data.data.length > 0) {
                  this.addeditprojecterror = dt.data.data[0];
                } else {
                  this.addeditprojecterror = {};
                }
                this.$store.commit("sbError", dt?.data?.message || CYCLE_MESSAGES.ERROR.UNKNOWN);
              }
            } catch (err) {
              this.$store.commit("sbError", err?.response?.data?.message || err?.message || err || CYCLE_MESSAGES.ERROR.UNKNOWN);
              console.log(err);
            } finally {
              this.addprojectloading = false;
            }
          },
            async editProject() {
            this.editprojectloading = true;
            if (!this.addeditproject.from || !this.addeditproject.to || !this.addeditproject.description || this.addeditproject.description.trim() === '') {
              this.addeditprojecterror = {
              from: !this.addeditproject.from ? CYCLE_MESSAGES.ERROR.PLEASE_SELECT_START_DATE : "",
              to: !this.addeditproject.to ? CYCLE_MESSAGES.ERROR.PLEASE_SELECT_END_DATE : "",
              description: !this.addeditproject.description || this.addeditproject.description.trim() === '' ? CYCLE_MESSAGES.ERROR.PLEASE_ENTER_DESCRIPTION : ""
              };
              this.editprojectloading = false;
              return;
            }
            try {
              const dt = await this.$store.dispatch('project/editProject', {
                id: this.addeditproject._id,
                data: this.addeditproject,
              });
              if (dt.data.status === "success") {
              this.$store.commit("sbSuccess", CYCLE_MESSAGES.SUCCESS.PROJECT_UPDATED);
              const updatedId = dt?.data?.data?.[0]?._id;
              if (updatedId && this.$store.state.erpEnabled.tbErpEnabled) {
                await this.$store.dispatch('project/syncERP', { payload: this.addeditproject, id: updatedId });
              }
              if (updatedId) {
                const index = this.listdata.findIndex(item => item._id === updatedId);
                if (index !== -1) this.listdata[index] = dt.data.data[0];
              }
              this.editprojectdialog = false;
              this.refreshData();
              } else {
              if (dt?.data?.data?.length > 0 && dt.data.data[0]?.index0) {
                this.addeditprojecterror = dt.data.data[0].index0;
              } else {
                throw new Error(dt.data.message);
              }
              }
            } catch (err) {
              this.$store.commit("sbError", err.message || err || CYCLE_MESSAGES.ERROR.UNKNOWN);
              console.log(err);
            } finally {
              this.editprojectloading = false;
            }
            },
          async diableenableNotification(id, type) {
            this.loading = true;
            try {
              await this.$store.dispatch('project/toggleNotification', { id, type });
              this.refreshData();
            } catch (err) {
              console.log('[Projects]: diableenableNotification error', err);
            } finally {
              this.loading = false;
            }
          },
          async closeProject(k, i) {
            this.loading = true;
            try {
              await this.$store.dispatch('project/closeProject', i);
              this.listdata[k].stage = 3;
              this.listdata.push({});
              this.listdata.pop();
              this.formateData();
            } catch (err) {
              console.log('[Projects]: closeProject error', err);
            } finally {
              this.loading = false;
            }
          },
          async openProject(k, i) {
            this.loading = true;
            try {
              await this.$store.dispatch('project/openProject', i);
              this.listdata[k].stage = 0;
              this.listdata.push({});
              this.listdata.pop();
              this.formateData();
            } catch (err) {
              console.log('[Projects]: openProject error', err);
            } finally {
              this.loading = false;
            }
          },
          generateDescription() {
            if (this.addeditproject.from && this.addeditproject.to) {
              if (
                !this.addeditproject.description ||
                this.generateddescription === this.addeditproject.description
              ) {
                this.generateddescription =
                  "Financial Close - " +
                  this.$nova.formatDate(this.addeditproject.from) +
                  " to " +
                  this.$nova.formatDate(this.addeditproject.to);
                this.addeditproject.description = this.generateddescription;
              }
            }
          },
          showPrompt() {
            // Get the position of the trigger button
            const xpathResult = document.evaluate("//div[contains(text(),'Chart of Accounts')]", document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null);
            const targetElement = xpathResult.singleNodeValue;
            if (targetElement) {
              // Calculate the position of the prompt relative to the target component
              const targetRect = targetElement.getBoundingClientRect();
              const promptTop = targetRect.bottom + window.scrollY + 10; // Adjust as needed
              const promptLeft = targetRect.left + window.scrollX;
              this.promptStyles = { top: `${promptTop}px`, left: `${promptLeft}px` };
            }
          },
          hidePrompt() {
            this.isFirst = false;
          },
          // Apply card filters to a dataset
          applyCardFilters(data) {
            let result = [...data];
            if (this.filterSelectedData.selectedyear !== "All") {
              result = result.filter((project) => project.year === this.filterSelectedData.selectedyear);
            }
            if (this.filterSelectedData.selectedmonth !== "All") {
              result = result.filter((project) => project.month === this.filterSelectedData.selectedmonth);
            }
            if (this.filterSelectedData.selectedlocation.length > 0 && !this.isAllLocationsSelected) {
              result = result.filter((project) => {
                let locations = project.location || [];
                if (locations.length === 0) return false;
                return locations.some((locationId) => {
                  return this.filterSelectedData.selectedlocation.some(selectedLoc => 
                    selectedLoc._id === locationId
                  );
                });
              });
            }
            if (this.filterSelectedData.selecteduser !== "All") {
              result = result.filter((project) => {
                let users = [];
                project.userlist?.forEach((item) => users.push(item));
                project.managerlist?.forEach((item) => users.push(item));
                users = [...new Set(users)];
                return users.includes(this.filterSelectedData.selecteduser);
              });
            }
            return result;
          },
          // Apply table filters to a dataset
          applyTableFilters(data) {
            if (!this.selectfilter || Object.keys(this.selectfilter).length === 0) {
              return data;
            }
            return this.$nova.filterByDynamicCriteria(data, this.selectfilter);
          },
          // Unified method to apply all filters (card + table) in sequence
          applyAllFilters() {
            // Start with original data
            let result = [...this.formatedDataCopy];
            // Apply card filters first
            result = this.applyCardFilters(result);
            // Apply table filters on top of card-filtered data
            result = this.applyTableFilters(result);
            // Reset to page 1 first before updating filtered data to avoid reactivity issues
            this.page = 1;
            // Update filtered data
            this.$nextTick(() => {
              this.filteredData = result;
            });
          },
          filter() {
            this.applyAllFilters();
          },
          filterWithYear(year) {
            this.filterSelectedData.selectedyear = year
            this.filter()
          },
          filterByMonth(month) {
            this.filterSelectedData.selectedmonth = month
            this.filter();
          },
          filterByUser(user) {
            this.filterSelectedData.selecteduser = user
            this.filter();
          },
          toggleLocation(location) {
            const index = this.filterSelectedData.selectedlocation.findIndex(loc => loc._id === location._id);
            if (index > -1) {
              this.filterSelectedData.selectedlocation.splice(index, 1);
            } else {
              this.filterSelectedData.selectedlocation.push(location);
            }
            this.filter();
          },
          toggleAllLocations() {
            if (this.isAllLocationsSelected) {
              this.filterSelectedData.selectedlocation = [];
            } else {
              this.filterSelectedData.selectedlocation = this.filterData.location.filter(loc => loc !== 'All');
            }
            this.filter();
          },
          isLocationSelected(location) {
            return this.filterSelectedData.selectedlocation.some(loc => loc._id === location._id);
          },
          resetFilter() {
            // Reset card filters
            this.filterSelectedData.selecteduser = 'All';
            this.filterSelectedData.selectedmonth = 'All';
            this.filterSelectedData.selectedyear = 'All';
            this.filterSelectedData.selectedlocation = [];
            // Reset table filters
            this.selectfilter = {};
            // Clear the DataTableAction component's internal filter state
            if (this.$refs.dataTable) {
              // Clear internal selected filters
              this.$refs.dataTable.selectedFilters = {};
              // Clear persisted filter from store
              if (this.$refs.dataTable.clearPersistedFilter) {
                this.$refs.dataTable.clearPersistedFilter();
              }
              // Close any open filter menus
              this.$refs.dataTable.menuOpen = [];
              this.$refs.dataTable.filterStage = 0;
            }
            // Reset filtered data to original
            this.filteredData = [...this.formatedDataCopy];
            // Reset to page 1
            this.page = 1;
          },
          // gotoUserConfig() {
          //   let url = "";
          //   let fltr = {};
          //   Promise.resolve().then(() => {
          //     this.addprojectloading = true;
          //     if (this.addeditproject.checklistfrom === "checklistmaster") {
          //       url = "/v2/financialclose/project/getmasteruserlist/";
          //       if(this.addeditproject.checklistfromvalue instanceof Array && this.addeditproject.checklistfromvalue.length > 0) fltr = { tags: this.addeditproject.checklistfromvalue };
          //       else throw Error("No tags selected");
          //     }
          //     else if (this.addeditproject.checklistfrom === "previousproject") {
          //       url = "/v2/financialclose/project/getprojectuserlist/" + this.addeditproject.checklistfromvalue;
          //       if(!this.addeditproject.checklistfromvalue) fltr = { project: this.addeditproject.checklistfromvalue };
          //       else throw Error("No project selected");
          //     }
          //     if (url) return this.axios.post(url, {filter: fltr});
          //     else throw new Error("Error getting the user information");
          //   }).then(dt => {
          //     if (dt.data.status === "success") {
          //       this.checklistusers = dt.data.data[0].users;
          //       this.informationowners = dt.data.data[0].infoowners;
          //       this.newchecklistusers = JSON.parse(JSON.stringify(this.checklistusers));
          //       this.newinformationowners = JSON.parse(JSON.stringify(this.informationowners));
          //       // for (const k in this.checklistusers) {
          //       //   if (Object.hasOwnProperty.call(this.checklistusers, k)) {
          //       //     const el = this.checklistusers[k];
          //       //     if(this.userlist.indexOf(el._id) === -1 && this.managerlist.indexOf(el._id) === -1) this.userlist.push(el._id);
          //       //   }
          //       // }
          //       this.addprojectstepper = 3;
          //     }
          //   }).catch(err => {
          //     this.$store.commit("sbError", err.message || err || "Unknown error!");
          //     console.log(err);
          //   }).finally(() => {
          //     this.loading = false;
          //     this.addprojectloading = false;
          //   });
          // },
          // updateUser(v, from, type="remove") {
          //   if(from === "manager"){
          //     let ui = this.managerlist.indexOf(v);
          //     if(ui > -1) {
          //       this.managerlist.splice(ui, 1);
          //       if(type === "move") this.userlist.push(v);
          //     }
          //   }
          //   else if(from === "general"){
          //     let ui = this.userlist.indexOf(v);
          //     if(ui > -1) {
          //       this.userlist.splice(ui, 1);
          //       if(type === "move") this.managerlist.push(v);
          //     }
          //   }
          // },
          filterPart(item, header) {
            console.log('[ProjectNew]: filterPart', item, header);
            // Store the table filter criteria
            this.selectfilter = item;
            // Apply all filters (card + table) together
            this.applyAllFilters();
          },
          clearPart(item) {
            console.log('[ProjectNew]: clearPart', item);
            // Clear table filter criteria
            this.selectfilter = {};
            // Apply all filters (only card filters will apply now)
            this.applyAllFilters();
          },
        getLocationById(locationId) {
          return this.LocationsList.find(location => location._id === locationId);
        },
        getProgressWidth(summary, field) {
          const count = (summary || {}).count || 0;
          if (count === 0) return 0;
          return (((summary || {})[field] || 0) / count) * 100;
        },
        getCycleStatusConfig(status) {
          const configs = {
            [CYCLE_CREATION_STATUS.PENDING]: { label: 'Setting Up', bgColor: '#E3F2FD', textClass: 'blue--text text--darken-2', icon: 'mdi-timer-sand' },
            [CYCLE_CREATION_STATUS.IN_PROGRESS]: { label: 'Setting Up', bgColor: '#FFF3E0', textClass: 'orange--text text--darken-3', icon: 'mdi-progress-clock' },
            [CYCLE_CREATION_STATUS.FAILED]: { label: 'Setup Failed', bgColor: '#FFEBEE', textClass: 'red--text text--darken-2', icon: 'mdi-alert-circle-outline' },
          };
          return configs[status] || { label: status, bgColor: 'grey lighten-3', textClass: 'grey--text text--darken-1', icon: 'mdi-information-outline' };
        },
        isCycleSettingUp(status) {
          return status === CYCLE_CREATION_STATUS.PENDING || status === CYCLE_CREATION_STATUS.IN_PROGRESS;
        },
        async retryCycleCreation(projectId) {
          this.loading = true;
          try {
            await this.$store.dispatch('project/retryCycleCreation', projectId);
            this.refreshData();
          } catch (err) {
            console.error('[Projects]: retryCycleCreation error', err);
          } finally {
            this.loading = false;
          }
        },
        async deleteProject(projectId) {
          this.loading = true;
          try {
            await this.$store.dispatch('project/deleteProject', projectId);
            this.refreshData();
          } catch (err) {
            console.error('[Projects]: deleteProject error', err);
          } finally {
            this.loading = false;
          }
        },
        },
        beforeDestroy() {
          if (this.pollingTimer) {
            clearTimeout(this.pollingTimer);
            this.pollingTimer = null;
          }
        },
        deactivated() {
          if (this.pollingTimer) {
            clearTimeout(this.pollingTimer);
            this.pollingTimer = null;
          }
        },
        watch: {
          // Reset page to 1 when filtered data changes to avoid being stuck on invalid page
          filteredData: {
            handler(newVal, oldVal) {
              // Only reset if the data actually changed (not just a reference change)
              if (newVal !== oldVal) {
                // Ensure page is within valid bounds
                const maxPage = Math.ceil((newVal?.length || 0) / this.itemsPerPage) || 1;
                if (this.page > maxPage) {
                  this.page = 1;
                }
              }
            },
            immediate: false
          },
          // userlist: function (d) {
          //   this.addeditproject.userlist = d;
          // },
          // managerlist: function (d) {
          //   this.addeditproject.managerlist = d;
          // },
          "addeditproject.from": function () {
            this.generateDescription();
          },
          "addeditproject.to": function () {
            this.generateDescription();
          },
          addprojectdialog: function (d) {
            if (!d) this.$nova.gotoLink({ hash: "" });
          },
          checklistfrom: function (d) {
            this.addeditproject.checklistfrom = d;
            this.selectedCycleLocations = [];
            if (d === 'checklistmaster') {
              this.checklistfromvalue = [];
            } else {
              this.checklistfromvalue = null;
            }
          },
          checklistfromvalue: function (d) {
            this.addeditproject.checklistfromvalue = d;
            if (Array.isArray(d) && d.length > 0 && this.addeditprojecterror.tags) {
              this.$delete(this.addeditprojecterror, 'tags');
            }
            if (d && !Array.isArray(d) && this.addeditprojecterror.project) {
              this.$delete(this.addeditprojecterror, 'project');
            }
          },
          selectedCycleType: function(val) {
            if (val && this.addeditprojecterror.cycleType) {
              this.$delete(this.addeditprojecterror, 'cycleType');
            }
          }
          
        },
        computed: {
          filteredUserList() {
            return this.filterData.userlist.filter(user => {
              return (this.userSearch === '' || (this.userdata[user] && this.userdata[user].name.toLowerCase().includes(this.userSearch.toLowerCase())) ) && this.activeUserIds.includes(user);
            });
          },
          isAllLocationsSelected() {
            const availableLocations = this.filterData.location.filter(loc => loc !== 'All');
            return availableLocations.length > 0 && this.filterSelectedData.selectedlocation.length === availableLocations.length;
          },
          pagecount() {
            return Math.ceil(this.filteredData?.length / this.itemsPerPage) || 1;
          },
          // Check if any card filters are active
          hasCardActiveFilter() {
            return this.filterSelectedData.selectedyear !== 'All' ||
                   this.filterSelectedData.selectedmonth !== 'All' ||
                   this.filterSelectedData.selecteduser !== 'All' ||
                   this.filterSelectedData.selectedlocation.length > 0;
          },
          // Check if any table filters are active
          hasTableActiveFilter() {
            return Object.values(this.selectfilter || {}).some(v => Array.isArray(v) && v.length > 0);
          },
          // Check if any filters (card or table) are active
          hasAnyActiveFilter() {
            return this.hasCardActiveFilter || this.hasTableActiveFilter;
          },
        }
      };
</script>

<style scoped>
    .balloon-prompt {
        position: absolute;
        background-color: #ffffff;
        border: 1px solid #ffffff;
        padding: px;
        border-radius: 3px;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
      }
      
      .close-btn-container {
        position: absolute;
        top: 5px;
        right: 5px;
        width: 30px;
        height: 30px;
        background-color: #ccc;
        /* Gray color */
        border-radius: 50%;
        /* Circle shape */
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
      }
      
      .close-btn {
        color: #fff;
        font-size: 20px;
      }
      
      button {
        padding: 10px 20px;
        font-size: 16px;
        cursor: pointer;
      }

      .cycle-status-badge.v-chip {
        padding: 2px 6px;
        font-weight: 500;
        font-size: 11px;
        letter-spacing: 0.02em;
        border-radius: 4px;
        height: 22px;
      }
</style>